/**
 * Wishlist (favorites) feature.
 *
 * Stores an array of product handles in localStorage so it works without a
 * customer account, keeps every heart button and the header counter in sync
 * on the current page, and stays in sync across browser tabs via the
 * "storage" event.
 *
 * Exposes `window.Wishlist` with a small API (all/has/add/remove/toggle) and
 * defines two custom elements used across the theme:
 *   - <wishlist-toggle>  a heart button that adds/removes a single product
 *   - <wishlist-count>   a badge that mirrors the number of saved products
 */
(function () {
  if (window.Wishlist) return;

  var STORAGE_KEY = 'ag:wishlist';
  var EVENT_NAME = 'wishlist:updated';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      // localStorage unavailable (private mode, quota exceeded, etc.) — fail silently.
    }
    notify(handles);
  }

  function notify(handles) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { handles: handles, count: handles.length } })
    );
  }

  var Wishlist = {
    all: function () {
      return readWishlist();
    },
    count: function () {
      return readWishlist().length;
    },
    has: function (handle) {
      if (!handle) return false;
      return readWishlist().indexOf(handle) !== -1;
    },
    add: function (handle) {
      if (!handle) return;
      var handles = readWishlist();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        writeWishlist(handles);
      }
    },
    remove: function (handle) {
      if (!handle) return;
      var handles = readWishlist().filter(function (item) {
        return item !== handle;
      });
      writeWishlist(handles);
    },
    toggle: function (handle) {
      if (!handle) return false;
      var isSaved = this.has(handle);
      if (isSaved) {
        this.remove(handle);
      } else {
        this.add(handle);
      }
      return !isSaved;
    },
  };

  window.Wishlist = Wishlist;

  // Keep other tabs/windows showing the same store in sync.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    notify(readWishlist());
  });

  /**
   * <wishlist-toggle data-product-handle="..."> wraps a heart button that
   * adds/removes a product from the wishlist and reflects its saved state.
   */
  function defineWishlistToggle() {
    class WishlistToggleElement extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-wishlist-button]') || this;
        this.handle = this.getAttribute('data-product-handle');

        this._onClick = this._onClick.bind(this);
        this._onUpdate = this._onUpdate.bind(this);

        this.button.addEventListener('click', this._onClick);
        document.addEventListener(EVENT_NAME, this._onUpdate);

        this._render();
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this._onClick);
        document.removeEventListener(EVENT_NAME, this._onUpdate);
      }

      _onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.handle) return;

        var nowSaved = window.Wishlist.toggle(this.handle);
        this._render(nowSaved);

        this.classList.remove('wishlist-toggle--animating');
        // Force reflow so the animation can restart on repeated clicks.
        void this.offsetWidth;
        this.classList.add('wishlist-toggle--animating');
      }

      _onUpdate() {
        this._render();
      }

      _render(forceState) {
        var isSaved = typeof forceState === 'boolean' ? forceState : window.Wishlist.has(this.handle);
        if (this.button) this.button.setAttribute('aria-pressed', String(isSaved));
        this.classList.toggle('wishlist-toggle--active', isSaved);

        var label = isSaved ? this.getAttribute('data-label-remove') : this.getAttribute('data-label-add');
        if (label && this.button) this.button.setAttribute('aria-label', label);
      }
    }

    if (!customElements.get('wishlist-toggle')) {
      customElements.define('wishlist-toggle', WishlistToggleElement);
    }
  }

  /**
   * <wishlist-count> shows the number of saved products (e.g. in the
   * header), hiding itself when the wishlist is empty — mirrors the
   * existing cart bubble pattern.
   */
  function defineWishlistCount() {
    class WishlistCountElement extends HTMLElement {
      connectedCallback() {
        this._onUpdate = this._onUpdate.bind(this);
        document.addEventListener(EVENT_NAME, this._onUpdate);
        this._render();
      }

      disconnectedCallback() {
        document.removeEventListener(EVENT_NAME, this._onUpdate);
      }

      _onUpdate() {
        this._render();
      }

      _render() {
        var count = window.Wishlist.count();
        var textEl = this.querySelector('[data-wishlist-count-text]');
        if (textEl) textEl.textContent = count > 99 ? '99+' : String(count);
        this.classList.toggle('hidden', count === 0);
      }
    }

    if (!customElements.get('wishlist-count')) {
      customElements.define('wishlist-count', WishlistCountElement);
    }
  }

  defineWishlistToggle();
  defineWishlistCount();
})();
